import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Radio } from "lucide-react";

/**
 * Shared layout for legal pages (Terms of Service, Privacy Policy, etc.).
 * Provides the same dark-themed shell as the landing page so legal URLs
 * feel like first-class parts of the product rather than a separate site.
 */
export default function LegalLayout({ title, eyebrow, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-bg text-ink-primary">
      <nav className="sticky top-0 z-40 border-b border-line bg-bg/70 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-home-link">
            <img
              src="/brand/broadcastflowai-logo.png"
              alt="BroadcastFlow AI"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <div className="hidden sm:flex items-center gap-6 text-xs font-mono uppercase tracking-wider text-ink-muted">
            <Link to="/terms" className="hover:text-brand-amber transition" data-testid="legal-nav-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-brand-amber transition" data-testid="legal-nav-privacy">Privacy</Link>
            <Link to="/refunds" className="hover:text-brand-amber transition" data-testid="legal-nav-refunds">Refunds</Link>
            <Link to="/cookies" className="hover:text-brand-amber transition" data-testid="legal-nav-cookies">Cookies</Link>
            <Link
              to="/login"
              className="rounded-md border border-line bg-bg-surface px-3 py-1.5 text-ink-primary hover:border-brand-amber/40 transition"
              data-testid="legal-nav-login"
            >
              Sign in
            </Link>
          </div>
        </div>
      </nav>

      <header className="border-b border-line bg-bg-surface/50">
        <div className="mx-auto max-w-3xl px-6 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-ink-muted hover:text-brand-amber transition mb-4"
            data-testid="legal-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          {eyebrow && (
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-brand-amber mb-3">
              <Radio className="h-3 w-3" /> {eyebrow}
            </div>
          )}
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter" data-testid="legal-title">
            {title}
          </h1>
          {lastUpdated && (
            <div className="mt-3 text-xs font-mono text-ink-muted" data-testid="legal-updated">
              Last updated: {lastUpdated}
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12 legal-body">
        {children}
      </main>

      <footer className="border-t border-line bg-bg-surface py-10">
        <div className="mx-auto max-w-7xl px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-ink-muted text-xs">© 2026 BroadcastFlow AI · Built for broadcasters</span>
          <div className="flex flex-wrap items-center gap-5 text-xs font-mono uppercase tracking-wider text-ink-muted">
            <Link to="/terms" className="hover:text-brand-amber transition">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-brand-amber transition">Privacy Policy</Link>
            <Link to="/refunds" className="hover:text-brand-amber transition">Refunds</Link>
            <Link to="/cookies" className="hover:text-brand-amber transition">Cookies</Link>
            <a href="mailto:legal@broadcastflow.ai" className="hover:text-brand-amber transition">legal@broadcastflow.ai</a>
          </div>
        </div>
      </footer>

      {/* Styles for the long-form legal copy — kept inline so the
          single Tailwind tree doesn't have to learn typography utilities */}
      <style>{`
        .legal-body { color: var(--ink-secondary, #b9bcc4); line-height: 1.7; font-size: 15px; }
        .legal-body h2 { color: var(--ink-primary, #ededee); font-weight: 800; font-size: 1.5rem; letter-spacing: -0.02em; margin: 2.25rem 0 0.75rem; font-family: var(--font-display, system-ui); }
        .legal-body h3 { color: var(--ink-primary, #ededee); font-weight: 700; font-size: 1.05rem; margin: 1.5rem 0 0.5rem; }
        .legal-body p { margin: 0 0 1rem; }
        .legal-body ul { margin: 0 0 1rem; padding-left: 1.25rem; list-style: disc; }
        .legal-body li { margin: 0.25rem 0; }
        .legal-body strong { color: var(--ink-primary, #ededee); font-weight: 600; }
        .legal-body code { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); padding: 1px 5px; border-radius: 4px; font-size: 0.85em; }
        .legal-body a { color: rgb(245 158 11); text-decoration: underline; text-underline-offset: 3px; }
        .legal-body a:hover { color: rgb(252 211 77); }
      `}</style>
    </div>
  );
}
